/**
 * A date box that reads DD/MM/YYYY whatever the browser's language.
 *
 * `<input type="date">` draws its text in the browser's locale, so an
 * en-US browser shows 04/18/2024 and nothing a page does changes that. The
 * hotel writes dates day first, so the text here is a plain box in
 * DD/MM/YYYY (and HH:mm for a date and time) while the value that goes in and
 * out stays ISO — `2024-04-18`, `2024-04-18T13:05` — exactly what the native
 * picker used to hand back. The calendar button still opens the browser's own
 * picker, on a hidden native input underneath.
 *
 * A value that is not ISO (what the scanner read, or a half-typed date) is
 * shown as it is and passed through unchanged, so it is never blanked.
 */

import { forwardRef, useEffect, useRef, useState } from 'react'
import { CalendarDays } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fieldStyles } from '@/components/ui/primitives'

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const ISO_DATETIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/
const DMY = /^(\d{1,2})[/.\- ](\d{1,2})[/.\- ](\d{4})$/
const DMY_TIME = /^(\d{1,2})[/.\- ](\d{1,2})[/.\- ](\d{4})[ T,]+(\d{1,2}):(\d{2})$/

const pad = (n: number | string) => String(n).padStart(2, '0')

function validDay(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false
  return day <= new Date(year, month, 0).getDate()
}

/** ISO → the text the box shows; anything else is shown as it is. */
export function isoToDisplay(value: string, withTime = false): string {
  if (!value) return ''
  if (withTime) {
    const m = ISO_DATETIME.exec(value)
    return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : value
  }
  const m = ISO_DATE.exec(value)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : value
}

/** Typed text → ISO when it is a real date, otherwise the text itself. */
export function displayToIso(text: string, withTime = false): string {
  const trimmed = text.trim()
  if (!trimmed) return ''
  if (withTime) {
    const m = DMY_TIME.exec(trimmed)
    if (m) {
      const [day, month, year, hour, minute] = m.slice(1).map(Number)
      if (validDay(year, month, day) && hour < 24 && minute < 60) {
        return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`
      }
    }
    return text
  }
  const m = DMY.exec(trimmed)
  if (m) {
    const [day, month, year] = m.slice(1).map(Number)
    if (validDay(year, month, day)) return `${year}-${pad(month)}-${pad(day)}`
  }
  return text
}

/**
 * Put the separators in while someone types digits: `18042024` becomes
 * `18/04/2024`. Only digits-and-separators text is touched, so anything else
 * a person types is left alone.
 */
export function maskDate(text: string, withTime = false): string {
  if (!/^[\d/ :]*$/.test(text)) return text
  const digits = text.replace(/\D/g, '').slice(0, withTime ? 12 : 8)
  let out = digits.slice(0, 2)
  if (digits.length > 2) out += `/${digits.slice(2, 4)}`
  if (digits.length > 4) out += `/${digits.slice(4, 8)}`
  if (withTime && digits.length > 8) out += ` ${digits.slice(8, 10)}`
  if (withTime && digits.length > 10) out += `:${digits.slice(10, 12)}`
  return out
}

type NativeProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange' | 'defaultValue'
>

interface Props extends NativeProps {
  value: string
  onChange: (value: string) => void
  /** Date and time (`YYYY-MM-DDTHH:mm`) rather than a date alone. */
  withTime?: boolean
  invalid?: boolean
}

export const DateInput = forwardRef<HTMLInputElement, Props>(function DateInput(
  { value, onChange, withTime = false, invalid, className, disabled, placeholder, ...rest },
  ref,
) {
  const [text, setText] = useState(() => isoToDisplay(value, withTime))
  // The last value this box emitted: a value arriving from outside that is
  // not it (a reset, the picker, a new field) replaces what is typed.
  const emitted = useRef(value)
  const native = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value
      setText(isoToDisplay(value, withTime))
    }
  }, [value, withTime])

  const emit = (next: string) => {
    emitted.current = next
    onChange(next)
  }

  const nativeValue = withTime
    ? ISO_DATETIME.test(value)
      ? value.slice(0, 16)
      : ''
    : ISO_DATE.test(value)
      ? value
      : ''

  const openPicker = () => {
    const picker = native.current
    if (!picker) return
    try {
      picker.showPicker()
    } catch {
      picker.focus()
    }
  }

  return (
    <div className="relative w-full">
      <input
        {...rest}
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        disabled={disabled}
        value={text}
        placeholder={placeholder ?? (withTime ? 'DD/MM/YYYY HH:MM' : 'DD/MM/YYYY')}
        onChange={(event) => {
          const raw = event.target.value
          // Only mask while the text grows, so backspacing over a slash works.
          const next = raw.length > text.length ? maskDate(raw, withTime) : raw
          setText(next)
          emit(displayToIso(next, withTime))
        }}
        className={cn(
          'h-9 w-full px-3 text-sm',
          fieldStyles,
          invalid ? 'border-[var(--danger)]' : 'border-[var(--border-strong)]',
          className,
          'tabular pe-[3em]',
        )}
      />
      <input
        ref={native}
        type={withTime ? 'datetime-local' : 'date'}
        tabIndex={-1}
        aria-hidden
        disabled={disabled}
        value={nativeValue}
        onChange={(event) => {
          const next = event.target.value
          setText(isoToDisplay(next, withTime))
          emit(next)
        }}
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px w-full opacity-0"
      />
      <button
        type="button"
        aria-label="Open calendar"
        disabled={disabled}
        onClick={openPicker}
        className="absolute inset-y-0 end-0 grid aspect-square h-full place-items-center rounded-e-[10px] text-[var(--text-muted)] hover:text-[var(--text)] focus-visible:outline-none focus-visible:text-[var(--brand)] disabled:pointer-events-none"
      >
        <CalendarDays className="h-[42%] w-[42%]" aria-hidden />
      </button>
    </div>
  )
})
